import React, { useEffect, useMemo, useState } from 'react';
import { Drawer, Form, message, Select } from 'antd';
import { DismissalForm } from 'components/RecordForms/DismissalForm';
import { SalaryChangeForm } from 'components/RecordForms/SalaryChangeForm';
import { LeaveWithoutPayForm } from 'components/RecordForms/LeaveWithoutPayForm';
import { HospitalForm } from 'components/RecordForms/HospitalForm';
import { BonusForm } from 'components/RecordForms/BonusForm';
import { CompensationForm } from 'components/RecordForms/CompensationForm';
import { ReviewVacationForm } from 'components/RecordForms/ReviewVacationForm';
import { PaidVacationForm } from 'components/RecordForms/PaidVacationForm';
import { BusinessTripForm } from 'components/RecordForms/BusinessTripForm';
import { employeesRecordsMap } from 'utils/handbooks/employeeHandbooks';
import { EmployeesService } from 'services/EmployeesService';
import { REQUEST_LOADING } from 'constants/alerts';
import { requiredRule } from 'utils/forms/validationRules';
import moment from 'moment';

function EmployeesRecordsForm({
  isActive,
  initialValues,
  formKey,
  selectedClient,
  onClose,
  onSuccess,
}) {
  const isEditing = initialValues !== null;
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (initialValues) {
      const { worker_id } = initialValues;

      setSelectedEmployeeIds([worker_id]);
    }
  }, [initialValues]);

  useEffect(() => {
    if (!formKey) {
      setSelectedEmployeeIds([]);
    }
  }, [formKey]);

  useEffect(() => {
    if (selectedClient) {
      fetchClientEmployees();
    }
  }, [selectedClient]);

  const fetchClientEmployees = async () => {
    const employees = await EmployeesService.getAllEmployeesOfClient(
      selectedClient
    );

    if (employees && Array.isArray(employees)) {
      setEmployees(employees);
    }
  };

  const disableDate = useMemo(() => {
    const employeeIds = new Set(selectedEmployeeIds);
    const [firstEmployeeId] = employeeIds;
    const firstEmployee = employees.find(
      (employee) => employee?.id === firstEmployeeId
    );
    let maxHiredDate = firstEmployee?.date;

    employees?.forEach((employee) => {
      if (
        employeeIds.has(employee.id) &&
        moment(employee?.date).isBetween(maxHiredDate, moment())
      ) {
        maxHiredDate = employee?.date;
      }
    });

    return maxHiredDate;
  }, [selectedEmployeeIds]);

  const selectEmployee = (
    <Form.Item
      name={isEditing ? 'worker_id' : 'worker_ids'}
      label="Сотрудник"
      rules={[requiredRule]}
    >
      <Select
        allowClear
        showSearch
        mode={isEditing ? '' : 'multiple'}
        placeholder="Выбрать сотрудника"
        filterOption={(input, { children, key }) => {
          return (
            children?.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
            key?.toLowerCase().indexOf(input.toLowerCase()) >= 0
          );
        }}
        onChange={(value) => {
          if (typeof value === 'number') {
            setSelectedEmployeeIds([value]);
            return;
          }

          setSelectedEmployeeIds(value);
        }}
      >
        {employees?.map(({ id, full_name }) => (
          <Select.Option key={id} value={id}>
            {full_name}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );

  const renderRecordForm = () => {
    switch (formKey) {
      case 'dismissal':
        return (
          <DismissalForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </DismissalForm>
        );
      case 'salary_change':
        return (
          <SalaryChangeForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </SalaryChangeForm>
        );
      case 'paid_vacation':
        return (
          <PaidVacationForm
            initialValues={initialValues}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </PaidVacationForm>
        );
      case 'leave_without_pay':
        return (
          <LeaveWithoutPayForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </LeaveWithoutPayForm>
        );
      case 'hospital':
        return (
          <HospitalForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </HospitalForm>
        );
      case 'business_trip':
        return (
          <BusinessTripForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </BusinessTripForm>
        );
      case 'review_vacation':
        return (
          <ReviewVacationForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </ReviewVacationForm>
        );
      case 'bonus':
        return (
          <BonusForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </BonusForm>
        );
      case 'compensation':
        return (
          <CompensationForm
            initialValues={initialValues}
            isEditing={isEditing}
            disableDate={disableDate}
            onSubmit={handleSubmit}
          >
            {selectEmployee}
          </CompensationForm>
        );
      default:
        return null;
    }
  };

  const handleSubmit = (data) => {
    const payload = {
      ...data,
      record_type: formKey,
    };

    if (isEditing) {
      return handleUpdate(payload, initialValues?.id);
    }

    return handleCreate(payload);
  };

  const handleCreate = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await EmployeesService.createEmployeeRecord(payload);

      if (isCreated) {
        setSelectedEmployeeIds([]);
        message.success('Запись успешно добавлена');
        return onSuccess();
      }
    } catch (err) {
      message.error('Не удалось добавить запись');
    } finally {
      hide();
    }
  };

  const handleUpdate = async (payload, recordId) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await EmployeesService.updateEmployeeRecord(
        payload,
        recordId
      );

      if (isUpdated) {
        setSelectedEmployeeIds([]);
        message.success('Запись успешно отредактирована');
        return onSuccess();
      }
    } catch (err) {
      message.error('Не удалось отредактировать запись');
    } finally {
      hide();
    }
  };

  return (
    <Drawer
      title={
        isEditing
          ? `Редактирование записи (${employeesRecordsMap[formKey]})`
          : `Создание записи (${employeesRecordsMap[formKey]})`
      }
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      {renderRecordForm()}
    </Drawer>
  );
}

export { EmployeesRecordsForm };
